import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/**
 * Collapses Edit + Delete into a single "Actions" dropdown, styled to match
 * the Browse dropdown in App.js's top nav (same ghostBtn trigger, same menu
 * card). Edit and Delete keep their original distinct colors (purple / red)
 * once revealed inside the menu — only the always-visible footprint on the
 * page collapses down to one neutral button.
 *
 * Props:
 *   editPath        - route for the Edit link
 *   editLabel       - e.g. "Edit Artist"
 *   onDeleteClick   - called when the Delete item is clicked
 *   deleteLabel     - e.g. "Delete Artist"
 *   deleteDisabled  - true when has_existing_events blocks deletion
 *   deleteTooltip   - tooltip text shown when deleteDisabled is true
 */
export default function ActionsDropdown({
    editPath,
    editLabel,
    onDeleteClick,
    deleteLabel,
    deleteDisabled,
    deleteTooltip
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        function onOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    return (
        <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
            <button
                onClick={() => setOpen(v => !v)}
                style={{
                    ...S.ghostBtn,
                    color: '#bb86fc',
                    backgroundColor: open ? '#2c2c2c' : 'transparent',
                    borderColor: open ? '#555' : '#444',
                }}
            >
                Actions <span style={S.chevron}>{open ? '▴' : '▾'}</span>
            </button>
            {open && (
                <div style={S.menu}>
                    <Link
                        to={editPath}
                        style={S.editMenuItem}
                        onClick={() => setOpen(false)}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2a2a2a'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                        {editLabel}
                    </Link>
                    <button
                        onClick={() => {
                            if (deleteDisabled) return;
                            setOpen(false);
                            onDeleteClick();
                        }}
                        disabled={deleteDisabled}
                        title={deleteDisabled ? deleteTooltip : undefined}
                        style={{
                            ...S.deleteMenuItem,
                            ...(deleteDisabled ? S.deleteMenuItemDisabled : {})
                        }}
                        onMouseEnter={e => { if (!deleteDisabled) e.currentTarget.style.backgroundColor = '#2c1818'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                        {deleteLabel}
                    </button>
                </div>
            )}
        </div>
    );
}

const S = {
    ghostBtn: {
        background: 'transparent',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#ccc',
        fontSize: '14px',
        padding: '7px 13px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap',
    },
    chevron: { fontSize: '10px', opacity: 0.75 },
    menu: {
        position: 'absolute',
        top: 'calc(100% + 5px)',
        right: 0,
        left: 'auto',
        backgroundColor: '#1e1e1e',
        border: '1px solid #444',
        borderRadius: '6px',
        minWidth: '180px',
        zIndex: 300,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        overflow: 'hidden',
    },
    editMenuItem: {
        display: 'block',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: 'bold',
        textDecoration: 'none',
        color: '#bb86fc',
        cursor: 'pointer',
    },
    deleteMenuItem: {
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: 'bold',
        background: 'transparent',
        border: 'none',
        color: '#e57373',
        cursor: 'pointer',
    },
    deleteMenuItemDisabled: {
        color: '#7a4f4f',
        cursor: 'not-allowed',
    },
};
